import type { ActivePlayerFlag, Candidate, SlotKey } from './api';

export type ScorePolicy = {
  notPickedBonusPerStreak: number;
  notPickedMaxBonus: number;
  notRegisteredBonusPerStreak: number;
  notRegisteredMaxBonus: number;
  noShowPenaltyPerStreak: number;
  noShowMaxPenalty: number;
  latePenaltyPerStreak: number;
  lateMaxPenalty: number;
  subsOnlyBonusPerStreak: number;
  subsOnlyMaxBonus: number;
  yellowCardFactor: number;
  greenFlagFactor: number;
  csSlotPreferenceFactor: number;
};

// Fallback used before the policy has finished loading, and to seed the
// migration defaults. Keep in sync with 0020_score_policy.sql and
// 0027_not_registered_bonus.sql.
export const DEFAULT_SCORE_POLICY: ScorePolicy = {
  notPickedBonusPerStreak: 0.08,
  notPickedMaxBonus: 0.4,
  notRegisteredBonusPerStreak: 0.04,
  notRegisteredMaxBonus: 0.2,
  noShowPenaltyPerStreak: 0.15,
  noShowMaxPenalty: 0.6,
  latePenaltyPerStreak: 0.05,
  lateMaxPenalty: 0.2,
  subsOnlyBonusPerStreak: 0.04,
  subsOnlyMaxBonus: 0.2,
  yellowCardFactor: 0.9,
  greenFlagFactor: 2.5,
  csSlotPreferenceFactor: 1.1,
};

function yellowCount(c: Candidate): number {
  return (c.player.activeFlags ?? []).filter((f) => f.kind === 'yellow').length;
}

function activeFlagKinds(c: Candidate): Set<ActivePlayerFlag['kind']> {
  return new Set((c.player.activeFlags ?? []).map((f) => f.kind));
}

export function isRedCarded(c: Candidate): boolean {
  return activeFlagKinds(c).has('red') || yellowCount(c) >= 2;
}

export function hasGreenFlag(c: Candidate): boolean {
  return activeFlagKinds(c).has('green');
}

export type ForcedReason = 'r5' | 'r4' | 'neverSub';

export function forcedReason(c: Candidate): ForcedReason | null {
  if (isRedCarded(c)) return null;
  if (c.player.rank === 'R5') return 'r5';
  if (c.player.rank === 'R4') return 'r4';
  if (c.player.neverSub) return 'neverSub';
  return null;
}

export function isForcedPick(c: Candidate): boolean {
  return forcedReason(c) !== null;
}

/**
 * Green ("should-be-picked") is a soft force: picked before non-forced
 * candidates in Strong autofill, and never picked-last in Weak autofill.
 * Hard forces (R4/R5 / Never-Sub) still outrank green.
 */
export function isSoftForce(c: Candidate): boolean {
  return !isForcedPick(c) && !isRedCarded(c) && hasGreenFlag(c);
}

export type ScoreBreakdown = {
  score: number;
  base: number;
  streakFactor: number;
  notPickedBonus: number;
  notRegisteredBonus: number;
  subsOnlyBonus: number;
  noShowPenalty: number;
  latePenalty: number;
  yellow: boolean;
  green: boolean;
  slotBonus: boolean;
  redCarded: boolean;
  forced: ForcedReason | null;
  softForce: boolean;
};

export function scoreBreakdown(
  c: Candidate,
  policy: ScorePolicy,
  opts?: { slot?: SlotKey },
): ScoreBreakdown {
  const base = c.latestSnapshot?.m1Power ?? c.totalPower ?? 0;
  const m = c.metrics;
  const notPickedBonus = Math.min(
    policy.notPickedMaxBonus,
    m.consecutiveNotPicked * policy.notPickedBonusPerStreak,
  );
  const notRegisteredBonus = Math.min(
    policy.notRegisteredMaxBonus,
    (m.consecutiveNotRegistered ?? 0) * policy.notRegisteredBonusPerStreak,
  );
  const subsOnlyBonus = Math.min(
    policy.subsOnlyMaxBonus,
    m.consecutiveSubsOnly * policy.subsOnlyBonusPerStreak,
  );
  const noShowPenalty = Math.min(
    policy.noShowMaxPenalty,
    m.consecutiveNoShows * policy.noShowPenaltyPerStreak,
  );
  const latePenalty = Math.min(
    policy.lateMaxPenalty,
    m.consecutiveLates * policy.latePenaltyPerStreak,
  );
  const streakFactor = Math.max(
    0.1,
    1 + notPickedBonus + notRegisteredBonus + subsOnlyBonus - noShowPenalty - latePenalty,
  );

  const kinds = activeFlagKinds(c);
  const yellow = kinds.has('yellow');
  const green = kinds.has('green');
  let score = base * streakFactor;
  if (yellow) score *= policy.yellowCardFactor;
  if (green) score *= policy.greenFlagFactor;
  const slotBonus = !!(opts?.slot && c.player.preferredCsSlot === opts.slot);
  if (slotBonus) score *= policy.csSlotPreferenceFactor;

  return {
    score,
    base,
    streakFactor,
    notPickedBonus,
    notRegisteredBonus,
    subsOnlyBonus,
    noShowPenalty,
    latePenalty,
    yellow,
    green,
    slotBonus,
    redCarded: isRedCarded(c),
    forced: forcedReason(c),
    softForce: isSoftForce(c),
  };
}

export function selectionScore(
  c: Candidate,
  policy: ScorePolicy,
  opts?: { slot?: SlotKey },
): number {
  return scoreBreakdown(c, policy, opts).score;
}

export type AutofillMode = 'strong' | 'weak';

/**
 * Rank the applicants for a slot for autofill. Red-carded players are
 * dropped entirely. Order: hard forces → soft forces (green) → rest.
 */
export function rankApplicants(
  applicants: Candidate[],
  mode: AutofillMode,
  policy: ScorePolicy,
  opts?: { slot?: SlotKey },
): Candidate[] {
  const usable = applicants.filter((c) => !isRedCarded(c));
  const forced: Candidate[] = [];
  const softForced: Candidate[] = [];
  const rest: Candidate[] = [];
  for (const c of usable) {
    if (isForcedPick(c)) forced.push(c);
    else if (isSoftForce(c)) softForced.push(c);
    else rest.push(c);
  }
  const byScoreDesc = (a: Candidate, b: Candidate) =>
    selectionScore(b, policy, opts) - selectionScore(a, policy, opts);
  const byScoreAsc = (a: Candidate, b: Candidate) =>
    selectionScore(a, policy, opts) - selectionScore(b, policy, opts);
  forced.sort(byScoreDesc);
  softForced.sort(byScoreDesc);
  rest.sort(mode === 'strong' ? byScoreDesc : byScoreAsc);
  return [...forced, ...softForced, ...rest];
}

export function formatScore(n: number): string {
  if (n >= 100) return n.toFixed(0);
  if (n >= 10) return n.toFixed(1);
  return n.toFixed(2);
}

export function explainPickCategory(
  b: ScoreBreakdown,
): 'forced' | 'soft' | 'ranked' {
  if (b.forced) return 'forced';
  if (b.softForce) return 'soft';
  return 'ranked';
}
